import React, { useMemo } from 'react';
import type { BoxProps } from '@withjoy/joykit';
import { Box, TextV2, Flex, ButtonV2, TooltipV2, IconV2 } from '@withjoy/joykit';
import { ReactComponent as Info } from '@assets/icons/info.svg';
import { useTranslation } from '@shared/core';
import { ReactComponent as Warning } from '@assets/icons/Error-32.svg';
import type { CommerceMonetaryValue } from '../CreditCardSetUp.context';
import { useCurrencyFormatter } from '@shared/utils/currency';
import { useCreditCardSetupTelemetry } from '../CreditCardSetUp.telemetry';
import { useMount } from '@shared/utils/hooks/useMount';
import { DEFAULT_CURRENCY } from '@shared/utils/currency/constants';

/**
 * Fixed transfer fee for each payout
 * @const {number} TRANSFER_FEE - transfer fee in US dollars.
 */
const TRANSFER_FEE = 1.7;
export const PendingPayoutDialog: React.FC<{
  onClose: () => void;
  isPayoutAllowed: boolean;
  loadingPayoutRequest: boolean;
  withdrawBalance: () => void;
  availableBalance?: CommerceMonetaryValue | null;
  accountNumber: string;
}> = ({ onClose, isPayoutAllowed, loadingPayoutRequest, withdrawBalance, availableBalance, accountNumber }) => {
  const telemetry = useCreditCardSetupTelemetry();
  const { t2, t } = useTranslation('sharedRegistry');
  const { title, balance, transferFee, totalTransfer, feeTooltip, confirmTransfer, nevermind, balanceError } = t2('payoutDialog');
  const { formatCurrency } = useCurrencyFormatter();

  const { formattedBalanceAmount, feeFormatted, totalTransferFormatted } = useMemo(() => {
    const formattedBalanceAmount = formatCurrency({
      priceFloatingPointDecimalString: availableBalance?.valueInMajorUnits.toString() ?? '0',
      priceCurrencyCode: availableBalance?.currencyCode,
      formatForm: 'short',
    });
    const feeFormatted = formatCurrency({
      priceFloatingPointDecimalString: TRANSFER_FEE.toString(),
      priceCurrencyCode: availableBalance?.currencyCode,
      formatForm: 'short',
    });

    const totalTransferFormatted = formatCurrency({
      priceFloatingPointDecimalString: availableBalance?.valueInMajorUnits ? (availableBalance.valueInMajorUnits - TRANSFER_FEE).toString() : '0',
      priceCurrencyCode: availableBalance?.currencyCode,
      formatForm: 'short',
    });

    return { formattedBalanceAmount, feeFormatted, totalTransferFormatted };
  }, [formatCurrency, availableBalance]);

  useMount(() => {
    telemetry.payoutDialog.enter('NotStarted');
  });

  return (
    <>
      <Flex
        justifyContent="center"
        marginBottom={5}
      >
        <TextV2
          typographyVariant="display1"
          color="mono14"
        >
          {title}
        </TextV2>
      </Flex>
      <Flex
        alignItems="center"
        justifyContent="center"
        flexDirection="column"
      >
        <Flex justifyContent="center">
          <TextV2
            textAlign="center"
            typographyVariant="body3"
            color="mono12"
          >
            {t('payoutDialog').description({ accountNumber })}
          </TextV2>
        </Flex>
        <Flex
          flexDirection="column"
          minWidth="260px"
          borderStyle="solid"
          borderWidth="1px"
          borderColor="mono3"
          borderRadius={3}
          padding={6}
          marginTop={6}
          marginBottom={8}
        >
          <AccountRowDetail
            amountDescription={balance}
            amount={formattedBalanceAmount}
          />
          <AccountRowDetail
            amountDescription={transferFee}
            amount={feeFormatted}
            tooltip={feeTooltip}
          />
          <Box
            height="1px"
            backgroundColor="mono3"
            width="100%"
            marginY={6}
          />
          <AccountRowDetail
            amountDescription={totalTransfer}
            amount={totalTransferFormatted}
            bold={true}
          />
        </Flex>
        {!isPayoutAllowed && (
          <Flex
            alignItems="center"
            borderStyle="solid"
            borderWidth="1px"
            borderColor="negative3"
            borderRadius={3}
            backgroundColor="negative1"
            marginY={8}
            padding={5}
          >
            <Box marginRight={6}>
              <IconV2
                size="lg"
                color="negative5"
              >
                <Warning />
              </IconV2>
            </Box>
            <Box>
              <TextV2
                typographyVariant="label2"
                color="mono14"
              >
                {balanceError}
              </TextV2>
            </Box>
          </Flex>
        )}
        <Box>
          <ButtonV2
            onClick={() => {
              const currentBalance = availableBalance?.valueInMajorUnits || 0;
              telemetry.transferBalanceButtonClicked(currentBalance, availableBalance?.currencyCode || DEFAULT_CURRENCY, TRANSFER_FEE);
              withdrawBalance();
            }}
            size="lg"
            intent="neutral"
            shape="rounded"
            disabled={!isPayoutAllowed}
            loading={loadingPayoutRequest}
          >
            {confirmTransfer}
          </ButtonV2>
        </Box>

        <Box>
          <ButtonV2
            onClick={() => {
              telemetry.nevermindButtonClicked();
              onClose();
            }}
            size="lg"
            intent="neutral"
            shape="rounded"
            variant="ghost"
          >
            {nevermind}
          </ButtonV2>
        </Box>
      </Flex>
    </>
  );
};

type AccountRowTextStyleConfig = {
  typographyVariant: BoxProps['typographyVariant'];
  color: BoxProps['color'];
};
const ACCOUNT_ROW_TEXT_STYLES: Record<'default' | 'bold', AccountRowTextStyleConfig> = {
  default: { color: 'mono14', typographyVariant: 'hed1' },
  bold: { color: 'mono12', typographyVariant: 'body1' },
};

const AccountRowDetail: React.FC<{ amountDescription: string; amount: string; bold?: boolean; tooltip?: string }> = ({ amountDescription, amount, tooltip, bold = false }) => {
  const textStyles = ACCOUNT_ROW_TEXT_STYLES[bold ? 'bold' : 'default'];
  return (
    <Flex justifyContent="space-between">
      <Box>
        {tooltip ? (
          <Flex alignItems="center">
            <TextV2
              marginRight={2}
              {...textStyles}
            >
              {amountDescription}
            </TextV2>
            <TooltipV2
              placement="bottom"
              content={
                <TextV2
                  textAlign="center"
                  typographyVariant="label2"
                >
                  {tooltip}
                </TextV2>
              }
            >
              <IconV2 size="sm">
                <Info />
              </IconV2>
            </TooltipV2>
          </Flex>
        ) : (
          <>
            <TextV2 {...textStyles}>{amountDescription}</TextV2>
          </>
        )}
      </Box>
      <Box>
        <TextV2 {...textStyles}>{amount}</TextV2>
      </Box>
    </Flex>
  );
};
